import React, { useEffect, useState } from "react";
import "./ResponseListPage.css";

import {
    Box,
    Typography,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TablePagination,
    CircularProgress,
    Select,
    MenuItem,
    Button,
    Snackbar,
    SelectChangeEvent,
    Tooltip,
    TextField
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import {
    getUserResponse,
    deleteQuestionFromResponse,
    updateUserResponse,
    getRegisteredEventIds
} from "../../services/ResponseService";
import { getQuestionnaire } from "../../services/questionnaireService";
import { EventResponse, QuestionResponse } from "../../data/registration";
import { Question } from "../../data/questionnaire";
import { EventsListItem } from "../../data/eventInfo";
import UpdateResponseModal from "./UpdateResponseModal";
import ConfirmationModal from "./ConfirmationModal";

interface Props {
    userId: string;
    events: EventsListItem[];
}

const ResponseListPage: React.FC<Props> = ({ userId, events }) => {
    const [selectedEventId, setSelectedEventId] = useState<string>("");
    const [registeredEventIds, setRegisteredEventIds] = useState<string[]>([]);
    const [response, setResponse] = useState<EventResponse | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [showPaymentId, setShowPaymentId] = useState(false);

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [questionToEdit, setQuestionToEdit] = useState<QuestionResponse | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
    const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);

    const [successOpen, setSuccessOpen] = useState(false);

    useEffect(() => {
        const fetchRegisteredEvents = async () => {
            try {
                const ids = await getRegisteredEventIds(userId);
                setRegisteredEventIds(ids.map(id => id.toLowerCase()));
                if (ids.length === 1) {
                    setSelectedEventId(ids[0]);
                }
            } catch (error) {
                console.error("Failed to fetch registered events:", error);
            }
        };

        fetchRegisteredEvents();
    }, [userId]);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedEventId || !userId) return;

            setLoading(true);
            try {
                const [respData, qData] = await Promise.all([
                    getUserResponse(selectedEventId, userId),
                    getQuestionnaire(selectedEventId)
                ]);

                if (respData) setResponse(respData);
                if (qData?.questions) setQuestions(qData.questions);
                setError(null);
            } catch (err) {
                console.error(err);
                setError("Error loading data.");
            }
            setLoading(false);
        };

        fetchData();
    }, [selectedEventId, userId]);

    const handleEditClick = (question: QuestionResponse) => {
        setQuestionToEdit(question);
        setEditModalOpen(true);
    };

    const handleEditSave = async (updatedAnswers: string[]) => {
        if (!response || !questionToEdit) return;

        const updatedResponses = response.responses.map((r) =>
            r.questionId === questionToEdit.questionId ? { ...r, answers: updatedAnswers } : r
        );

        const updated = { ...response, responses: updatedResponses };
        const success = await updateUserResponse(updated);
        if (success) setResponse(updated);

        setEditModalOpen(false);
        setQuestionToEdit(null);
    };

    const handleDeleteClick = (questionId: string) => {
        setQuestionToDelete(questionId);
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!response || !questionToDelete) return;

        const success = await deleteQuestionFromResponse(response.eventId, response.userId, questionToDelete);

        if (success) {
            setResponse({
                ...response,
                responses: response.responses.filter((r) => r.questionId !== questionToDelete),
            });
        }

        setConfirmOpen(false);
        setQuestionToDelete(null);
    };

    const handleDeleteAllResponses = async () => {
        if (!response) return;

        const clearedResponses = response.responses.map((r) => ({
            ...r,
            answers: []
        }));

        const updated: EventResponse = {
            ...response,
            responses: clearedResponses
        };

        const success = await updateUserResponse(updated);

        if (success) {
            setResponse(updated);
            setDeleteAllConfirmOpen(false);
            setSuccessOpen(true);
        } else {
            alert("Failed to clear responses. Please try again.");
        }
    };

    const filteredEvents = events.filter(event =>
        registeredEventIds.includes(event.Id?.toLowerCase())
    );

    const selectedEvent = filteredEvents.find(
        e => e.Id?.toLowerCase() === selectedEventId.toLowerCase()
    );

    return (
        <Box className="response-container">
            <Typography variant="h4" className="response-title" sx={{ mt: 0, mb: 4 }}>
                My Registration Responses
            </Typography>

            {selectedEvent && (
                <Box className="response-event-info">
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Event: {selectedEvent.Name}
                    </Typography>

                    {response?.paymentId && (
                        <Box className="response-payment-container">
                            {!showPaymentId ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={() => setShowPaymentId(true)}
                                    className="response-view-payment-button"
                                >
                                    View Payment ID
                                </Button>
                            ) : (
                                <Box className="response-payment-display">
                                    <Tooltip title="This is your Stripe Payment ID" arrow>
                                        <TextField
                                            value={response.paymentId}
                                            size="small"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                            fullWidth
                                        />
                                    </Tooltip>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {filteredEvents.length > 1 && (
                <Select
                    value={selectedEventId}
                    onChange={(e: SelectChangeEvent<string>) => setSelectedEventId(e.target.value)}
                    displayEmpty
                    sx={{
                        mb: 4,
                        minWidth: 300,
                        maxWidth: 400,
                        backgroundColor: "#fafafa",
                        borderRadius: "8px",
                        padding: "10px",
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                        "& .MuiSelect-select": {
                            padding: "12px",
                            fontWeight: 600,
                            color: "#333",
                        },
                        "& fieldset": {
                            borderColor: "#ccc",
                        },
                        "&:hover fieldset": {
                            borderColor: "#999",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#1976d2",
                        }
                    }}
                >
                    {filteredEvents.map((event: EventsListItem) => {
                        const year = event.StartDate ? new Date(event.StartDate).getFullYear() : "Unknown Year";
                        return (
                            <MenuItem key={event.Id} value={event.Id}>
                                {event.Name} {year !== "Unknown Year" ? `(${year})` : ""}
                            </MenuItem>
                        );
                    })}
                </Select>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : response === null ? (
                <Typography className="response-empty-message">No response found for this event.</Typography>
            ) : response.responses.length === 0 ? (
                <Typography className="response-empty-message">No responses submitted yet.</Typography>
            ) : (
                <>
                    <TableContainer component={Paper} className="response-table">
                        <Table>
                            <TableHead className="response-table-header">
                                <TableRow>
                                    <TableCell className="response-table-cell-header">Question</TableCell>
                                    <TableCell className="response-table-cell-header">Answers</TableCell>
                                    <TableCell className="response-table-cell-header">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {response.responses
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((q: QuestionResponse) => {
                                        const prompt = questions.find(
                                            (qn: Question) => qn.id?.trim().toLowerCase() === q.questionId.trim().toLowerCase()
                                        )?.prompt || "Unknown question";

                                        return (
                                            <TableRow key={q.questionId} hover className="response-table-row">
                                                <TableCell sx={{ color: "#333", fontSize: "15px", fontWeight: 500 }}>
                                                    {prompt}
                                                </TableCell>
                                                <TableCell
                                                    align="left"
                                                    sx={{ pl: 2, color: "#333", fontSize: "15px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                                >
                                                    {q.answers.length > 0 ? q.answers.join(", ") : <i>No answer provided</i>}
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => handleEditClick(q)}
                                                        sx={{
                                                            color: "#1976d2",
                                                            "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.1)" },
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => handleDeleteClick(q.questionId)}
                                                        sx={{
                                                            color: "#d32f2f",
                                                            "&:hover": { backgroundColor: "rgba(211, 47, 47, 0.1)" },
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        className="response-pagination"
                        component="div"
                        count={response.responses.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />

                    <Box className="response-action-container">
                        <button
                            className="response-delete-all-button"
                            onClick={() => setDeleteAllConfirmOpen(true)}
                        >
                            <DeleteIcon />
                            Delete All Responses
                        </button>
                    </Box>
                </>
            )}

            <UpdateResponseModal
                open={editModalOpen}
                question={questionToEdit}
                onClose={() => setEditModalOpen(false)}
                onSave={handleEditSave}
            />

            <ConfirmationModal
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={confirmDelete}
                title="Delete Response"
                message="Are you sure you want to delete this question response?"
            />

            <ConfirmationModal
                isOpen={deleteAllConfirmOpen}
                onClose={() => setDeleteAllConfirmOpen(false)}
                onConfirm={handleDeleteAllResponses}
                title="Clear All Responses"
                message="Are you sure you want to clear all your answers for this event? Your registration will remain."
            />

            <Snackbar
                open={successOpen}
                autoHideDuration={3000}
                onClose={() => setSuccessOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <MuiAlert
                    onClose={() => setSuccessOpen(false)}
                    severity="success"
                    sx={{ width: "100%" }}
                    elevation={6}
                    variant="filled"
                >
                    All your responses have been cleared.
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default ResponseListPage;
